import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { observer } from '@react';
import { useDocument, useI18n } from '@react/hooks';
import { Badge, Card, ErrorAlert } from '@react/components';
import { documentStatusMap, formatDate, getStatusMapping } from '@/features/document/logic';

function snapshotText(snapshot: unknown): string {
  if (!snapshot) return '';
  try {
    const data = typeof snapshot === 'string' ? JSON.parse(snapshot) : snapshot;
    const blocks = Array.isArray((data as any)?.blocks) ? (data as any).blocks : [];
    return blocks.map((block: any) => {
      const richText = block.rich_text || block.richText || [];
      return richText.map((part: any) => part.plain_text || part.plainText || part.text?.content || '').join('');
    }).filter(Boolean).join('\n\n');
  } catch {
    return '';
  }
}

export const DocumentVersionDetailPage = observer(() => {
  const { id, versionId } = useParams<{ id: string; versionId: string }>();
  const store = useDocument();
  const { t, locale } = useI18n();
  const [item, setItem] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    if (!id || !versionId) return;
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    store.GetVersion('document', id, versionId).then((res: any) => {
      if (!cancelled) setItem(res?.version || res);
    }).catch((err: unknown) => {
      if (!cancelled) setError(err);
    }).finally(() => {
      if (!cancelled) setIsLoading(false);
    });
    return () => { cancelled = true; };
  }, [id, versionId]);

  const status = item?.status ? getStatusMapping(documentStatusMap, item.status) : null;
  const content = snapshotText(item?.content_snapshot || item?.snapshot);

  return <div className="mx-auto max-w-3xl space-y-5">
    <nav className="text-sm">
      <Link to={`/documents/${id}`} className="text-muted-foreground hover:text-foreground">{t('document.details')}</Link>
      <span className="mx-2 text-muted-foreground">/</span>
      <Link to={`/documents/${id}/versions`} className="text-muted-foreground hover:text-foreground">{t('document.versionHistory')}</Link>
      <span className="mx-2 text-muted-foreground">/</span>
      <span>{t('document.version.number')} {item?.version_number || ''}</span>
    </nav>
    {Boolean(error) && <ErrorAlert error={error} dismissible onDismiss={() => setError(null)} />}
    {isLoading ? <div className="py-12 text-center text-muted-foreground">{t('common.loading')}</div> : !item ?
      <p className="py-12 text-center text-muted-foreground">{t('common.notFound')}</p> : <>
        <div className="space-y-2">
          <h1 className="text-2xl font-semibold tracking-tight">{t('document.version.number')} {item.version_number ?? '—'}</h1>
          <p className="text-sm text-muted-foreground">{item.title || t('document.versionDetails')}</p>
        </div>
        <Card className="p-5">
          <dl className="grid grid-cols-1 sm:grid-cols-[10rem_1fr] gap-x-5 gap-y-4 text-sm">
            <dt className="text-muted-foreground">{t('document.fieldStatus')}</dt>
            <dd>{status ? <Badge variant={status.variant} size="sm">{t(status.labelKey)}</Badge> : '—'}</dd>
            <dt className="text-muted-foreground">{t('document.fieldCreatedAt')}</dt>
            <dd>{formatDate(item.created_at, locale)}</dd>
            {item.version_label && <><dt className="text-muted-foreground">{t('document.version.label')}</dt><dd>{item.version_label}</dd></>}
            {item.change_summary && <><dt className="text-muted-foreground">{t('document.version.changeSummary')}</dt><dd className="break-words">{item.change_summary}</dd></>}
            {item.approved_at && new Date(item.approved_at).getFullYear() > 1970 && <><dt className="text-muted-foreground">{t('document.version.approvedAt')}</dt><dd>{formatDate(item.approved_at, locale)}</dd></>}
          </dl>
        </Card>
        {content && <section className="space-y-3">
          <h2 className="text-base font-semibold">{t('document.version.content')}</h2>
          <Card className="p-6 whitespace-pre-wrap break-words text-sm leading-7">{content}</Card>
        </section>}
      </>}
  </div>;
});

export default DocumentVersionDetailPage;
